import { type ReactNode } from "react";

import "animate.css";
import clsx from "clsx";
import { type StepWizardChildProps } from "react-step-wizard";
import { twMerge } from "tailwind-merge";

import salesPlatformColumnLoading from "@assets/sales-v3-platform-loading-cell.gif";
import { GenericTooltip } from "@components/tooltip-container";

import { Loading } from "../loading";

export interface StepProps {
  readonly children: ReactNode;
  readonly title?: string;
  readonly loading?: boolean;
  readonly className?: string;
  readonly contentClassName?: string;
  readonly nextStep?: () => void;
  readonly nextStepButtonText?: string;
  readonly nextStepButtonTooltip?: React.ReactNode;
  readonly nextDisabled?: boolean;
  readonly loadingNextStep?: boolean;
}

export type StepChildProps = Partial<StepWizardChildProps> & {
  readonly isLast: boolean;
  readonly stepMetadata: any;
  readonly updateStepMetadata: (data: any) => void;
  readonly getStepMetadata: (stepName: string) => any | undefined;
  readonly isMarkedComplete?: boolean;
  readonly toggleStepComplete: () => void;
  readonly canVerifyAndFinish?: boolean;
  readonly unblockedFinishOnVerifyScreen?: boolean;
};

export const Step = ({
  children,
  title,
  contentClassName,
  loading,
  loadingNextStep,
  nextStep,
  nextStepButtonText,
  nextStepButtonTooltip,
  nextDisabled,
  ...props
}: StepProps) => (
  <>
    {loading ? (
      <Loading />
    ) : (
      <div
        {...props}
        className={clsx(
          "bg-white rounded-lg border border-solid border-gray-0",
          "my-0 min-h-[200px]",
          props.className,
        )}
      >
        {title && (
          <div className="mb-2">
            <h3 className="heading mx-0 mt-0 mb-2">{title}</h3>
            <hr />
          </div>
        )}
        <div className={clsx("flex flex-col", contentClassName)}>
          {children}
        </div>
        {nextStep && (
          <button
            onClick={() => {
              nextStep && nextStep();
            }}
            disabled={nextDisabled}
            className={clsx(
              "h-[78px] w-full px-10 py-6 bg-cosmic rounded-bl-lg rounded-br-lg" +
                "flex-col justify-center items-center gap-2 inline-flex",
              {
                "bg-cosmic+0  hover:bg-cosmic+1": !nextDisabled,
                "bg-cosmic-4": nextDisabled || loadingNextStep,
              },
            )}
          >
            {loadingNextStep ? (
              <div className="flex items-center h-full ml-2">
                <img
                  src={salesPlatformColumnLoading}
                  alt="Loading table"
                  style={{
                    height: "1.75rem",
                  }}
                />
              </div>
            ) : (
              <div
                className={clsx(
                  "justify-start items-center gap-6 inline-flex",
                  {
                    " cursor-not-allowed": nextDisabled,
                  },
                )}
              >
                <div className="flex-col justify-center items-center gap-2 inline-flex">
                  <div className="h-2.5 justify-center items-center gap-2 inline-flex">
                    <div
                      className={twMerge(
                        "text-white text-xs font-semibold uppercase leading-none tracking-wide",
                        clsx({ "text-disabled": nextDisabled }),
                      )}
                    >
                      Go to Next step
                    </div>
                  </div>
                  <div className="h-3 justify-center items-center gap-2 inline-flex">
                    <div
                      className={twMerge(
                        "text-white text-lg font-semibold leading-normal",
                        clsx({ "text-disabled": nextDisabled }),
                      )}
                    >
                      <GenericTooltip
                        content={nextStepButtonTooltip}
                        noInfoCircle
                        showTooltip={!!nextStepButtonTooltip}
                      >
                        {nextStepButtonText}
                      </GenericTooltip>
                    </div>
                  </div>
                </div>
                <i
                  className={twMerge(
                    "fa-sharp fa-regular fa-arrow-right fa-lg ml-3",
                    "text-white ",
                    clsx({ "text-disabled": nextDisabled }),
                  )}
                ></i>
              </div>
            )}
          </button>
        )}
      </div>
    )}
  </>
);
